/**
 * Geocoding service — Mapbox Geocoding API v6.
 *
 * Forward: text → coordinates.
 * Reverse: coordinates → address.
 *
 * Restricted to El Salvador (SV), Spanish locale.
 */
import { env } from '../env'
import { ServiceError } from '../errors/service-error'
import { fetchWithTimeout } from '../utils/fetch-with-timeout'

const MAPBOX_TOKEN = env.EXPO_PUBLIC_MAPBOX_TOKEN ?? ''
const BASE_URL = 'https://api.mapbox.com/search/geocode/v6'

/**
 * Geocoding is on the critical path of two busy latches that disable controls:
 * the listing wizard's "Continuar" (`isResolvingLocation`) and the checkout
 * location picker's "Guardar" (`isResolvingPin`). Both release in a `finally`,
 * so an unsettled request is a permanently dead button. Shorter than the 15s
 * default because a map pin the user just dragged is not worth a 15s stall —
 * the callers all fall back to the un-geocoded coordinates.
 */
const GEOCODING_TIMEOUT_MS = 8_000

export interface GeocodingResult {
  lat: number
  lng: number
  address: string
  /**
   * Nombre del lugar cuando Mapbox reconoce un POI ("Multiplaza", "Aeropuerto
   * Internacional de El Salvador"). Es el ancla que un huésped busca; la
   * dirección completa queda en `address` para el conductor.
   */
  name: string | null
  city: string | null
  country: string | null
}

interface MapboxFeature {
  geometry: {
    type: string
    coordinates: [number, number] // [lng, lat]
  }
  properties: {
    full_address?: string
    name?: string
    context?: {
      place?: { name?: string }
      country?: { name?: string }
    }
  }
}

interface MapboxResponse {
  type: string
  features: MapboxFeature[]
}

class GeocodingServiceImpl {
  /**
   * Forward geocode — text query to coordinates.
   * Limited to SV, max 5 results.
   */
  async forward(query: string): Promise<GeocodingResult[]> {
    if (!query.trim()) return []
    if (!MAPBOX_TOKEN) {
      throw new ServiceError({
        message: 'Mapbox token is not configured',
        code: 'GEOCODING_CONFIG_ERROR',
        statusCode: 500,
      })
    }

    const params = new URLSearchParams({
      q: query,
      access_token: MAPBOX_TOKEN,
      country: 'SV',
      limit: '5',
      language: 'es',
    })

    const response = await fetchWithTimeout(`${BASE_URL}/forward?${params.toString()}`, undefined, {
      timeoutMs: GEOCODING_TIMEOUT_MS,
    })

    if (!response.ok) {
      throw new ServiceError({
        message: `Geocoding forward failed: ${response.status}`,
        code: 'GEOCODING_FORWARD_ERROR',
        statusCode: response.status,
      })
    }

    const data = (await response.json()) as MapboxResponse
    return data.features.map(this.mapFeature)
  }

  /**
   * Reverse geocode — coordinates to address.
   * Returns null if no result found.
   */
  async reverse(lat: number, lng: number): Promise<GeocodingResult | null> {
    if (!MAPBOX_TOKEN) {
      throw new ServiceError({
        message: 'Mapbox token is not configured',
        code: 'GEOCODING_CONFIG_ERROR',
        statusCode: 500,
      })
    }

    const params = new URLSearchParams({
      longitude: String(lng),
      latitude: String(lat),
      access_token: MAPBOX_TOKEN,
      language: 'es',
    })

    const response = await fetchWithTimeout(`${BASE_URL}/reverse?${params.toString()}`, undefined, {
      timeoutMs: GEOCODING_TIMEOUT_MS,
    })

    if (!response.ok) {
      throw new ServiceError({
        message: `Geocoding reverse failed: ${response.status}`,
        code: 'GEOCODING_REVERSE_ERROR',
        statusCode: response.status,
      })
    }

    const data = (await response.json()) as MapboxResponse
    const first = data.features[0]
    if (!first) return null

    return this.mapFeature(first)
  }

  private mapFeature(feature: MapboxFeature): GeocodingResult {
    const [lng, lat] = feature.geometry.coordinates
    return {
      lat,
      lng,
      address: feature.properties.full_address ?? feature.properties.name ?? '',
      name: feature.properties.name ?? null,
      city: feature.properties.context?.place?.name ?? null,
      country: feature.properties.context?.country?.name ?? null,
    }
  }
}

export const geocodingService = new GeocodingServiceImpl()
